import React, { useState, useEffect, useRef } from 'react'
import InspectorDrawer from './InspectorDrawer'
import CommsDrawer from './CommsDrawer'

interface DrawerContainerProps {
  isOpen: boolean
  type: 'inspector' | 'comms' | null
  selectedTrack: string | null
  onClose: () => void
  targetRef?: React.RefObject<HTMLDivElement | HTMLButtonElement | null>
}

const DrawerContainer: React.FC<DrawerContainerProps> = ({ isOpen, type, selectedTrack, onClose, targetRef }) => {
  const [arrowPosition, setArrowPosition] = useState<number>(120)
  const drawerRef = useRef<HTMLDivElement>(null)

  const calculateArrowPosition = () => {
    if (!targetRef?.current || !drawerRef.current) {
      setArrowPosition(120)
      return
    }

    const targetRect = targetRef.current.getBoundingClientRect()
    const drawerRect = drawerRef.current.getBoundingClientRect()

    // Calculate the center of the target element relative to the drawer's left edge
    const targetCenterX = targetRect.left + targetRect.width / 2
    const drawerLeftX = drawerRect.left
    const offsetFromLeft = targetCenterX - drawerLeftX

    setArrowPosition(offsetFromLeft)
  }

  useEffect(() => {
    if (isOpen && type === 'comms') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        calculateArrowPosition()
      }, 300)
    }
  }, [isOpen, type, targetRef])

  useEffect(() => {
    const handleResize = () => {
      if (isOpen && type === 'comms') {
        calculateArrowPosition()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen, type])

  if (!isOpen || !type) return null

  return (
    <div data-drawer={type ?? undefined} className={`drawer-container ${isOpen ? 'open' : ''}`}>
      <div className="drawer-overlay" onClick={onClose} />
      <div 
        ref={drawerRef}
        className={`drawer ${type === 'comms' ? 'with-arrow' : ''}`}
        style={{ 
          '--arrow-left': `${arrowPosition}px`
        } as React.CSSProperties}
      >
        <button className="drawer-close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        {type === 'inspector' && <InspectorDrawer trackId={selectedTrack} onClose={onClose} />}
        {type === 'comms' && <CommsDrawer onClose={onClose} />}
      </div>
    </div>
  )
}

export default DrawerContainer
